import { strapiService } from './strapiClient'
import { useStrapi } from '@/composables/useStrapi'
import { getStrapiImageUrl } from '@/utils/strapi-utils'

// City data structure from Strapi v5 (flat structure)
export interface CityFromStrapi {
  id: number
  documentId: string
  title: string
  description: string
  createdAt: string
  updatedAt: string
  image: {
    url: string
    alt: string
  }
}

// Strapi v5 response types for cities
export interface CityResponse {
  data: CityFromStrapi[]
  meta: {
    pagination: {
      page: number
      pageSize: number
      pageCount: number
      total: number
    }
  }
}

export interface CitySingleResponse {
  data: CityFromStrapi
  meta: Record<string, unknown>
}

// Transformed City interface for frontend use
export interface City {
  label: string
  value: string
  description: string
  id: string
  documentId: string
  title: string
  image: {
    url: string
    alt: string
  }
}

// Utility function to transform Strapi city data to our City interface
const transformCity = (city: CityFromStrapi): City => ({
  id: city.id.toString(),
  documentId: city.documentId,
  title: city.title,
  label: city.title.charAt(0).toUpperCase() + city.title.slice(1).toLowerCase(),
  value: city.title.toLowerCase().replace(/\s+/g, '-'),
  description: city.description,
  image: {
    url: getStrapiImageUrl(city.image.url),
    alt: city.image.alt,
  },
})

/**
 * City Service - Provides both traditional async methods and reactive Vue composables
 *
 * Usage Examples:
 *
 * // In a Vue component with async/await:
 * const cities = await cityService.getCities()
 *
 * // In a Vue component with reactive queries:
 * const { data: cities, isLoading, error } = useCityService().useCities()
 *
 * // Get single city reactively:
 * const { data: city } = useCityService().useCity('city-document-id')
 *
 * // For admin operations:
 * const { createMutation } = useCityService()
 * await createMutation.mutateAsync({ title: 'New City', description: 'Description' })
 */
class CityService {
  /**
   * Get all cities (async method)
   * @returns Promise<City[]> - Array of transformed cities
   */
  async getCities(): Promise<City[]> {
    try {
      // Use strapiService directly for async methods
      const response = await strapiService.find('cities')
      // Transform Strapi data to match our City interface
      return response.data.map(transformCity)
    } catch (error) {
      console.error('Error fetching cities from Strapi:', error)
      // Throw error instead of returning fallback cities
      throw error
    }
  }

  /**
   * Get single city by documentId (async method)
   * @param documentId - Strapi document ID
   * @returns Promise<City | null> - Single city or null if not found
   */
  async getCityById(documentId: string): Promise<City | null> {
    try {
      // Use strapiService directly for async methods
      const response = await strapiService.findOne(
        'cities',
        documentId,
      )

      return transformCity(response.data)
    } catch (error) {
      console.error('Error fetching city by ID from Strapi:', error)
      return null
    }
  }

  /**
   * Get all cities including inactive ones (for admin purposes)
   * @returns Promise<City[]> - Array of all cities
   */
  async getAllCities(): Promise<City[]> {
    try {
      const response = await strapiService.find('cities')
      return response.data.map(transformCity)
    } catch (error) {
      console.error('Error fetching all cities from Strapi:', error)
      throw error
    }
  }
}

/**
 * Composable function for reactive city operations
 * Use this inside Vue components for reactive data and automatic caching
 *
 * @example
 * ```vue
 * <script setup>
 * const { useCities, useCity, createMutation } = useCityService()
 * const { data: cities, isLoading } = useCities()
 * </script>
 * ```
 */
export function useCityService() {
  const strapi = useStrapi()

  /**
   * Get reactive query for cities (for Vue components)
   * @param params - Optional query parameters
   * @returns Vue Query result with data, isLoading, error, etc.
   */
  const useCities = (params?: Record<string, unknown>) => {
    return strapi.useFind('cities', params)
  }

  /**
   * Get reactive query for single city (for Vue components)
   * @param documentId - Strapi document ID
   * @param params - Optional query parameters
   * @returns Vue Query result with data, isLoading, error, etc.
   */
  const useCity = (documentId: string, params?: Record<string, unknown>) => {
    return strapi.useFindOne('cities', documentId, params)
  }

  /**
   * Create a new city (for admin purposes)
   * @returns Mutation object for creating cities
   */
  const createMutation = strapi.useCreate('cities')

  /**
   * Update a city (for admin purposes)
   * @returns Mutation object for updating cities
   */
  const updateMutation = strapi.useUpdate('cities')

  /**
   * Delete a city (for admin purposes)
   * @returns Mutation object for deleting cities
   */
  const deleteMutation = strapi.useDelete('cities')

  return {
    // Reactive queries
    useCities,
    useCity,

    // Mutations
    createMutation,
    updateMutation,
    deleteMutation,

    // Direct service access
    service: strapi.service,
  }
}

// Export the singleton service instance
export default new CityService()
